import React from 'react';
import { BackButton } from '../components/common/BackButton';

interface PolicyPageProps {
  title: string;
  content: React.ReactNode;
}

function PolicyPage({ title, content }: PolicyPageProps) {
  return (
    <div className="min-h-screen pt-32 px-4">
      <div className="max-w-4xl mx-auto">
        <BackButton label="Back" />
        <div className="bg-black/50 border border-green-500/20 rounded-lg p-8">
          <h1 className="text-3xl font-bold text-white mb-8">{title}</h1>
          <div className="prose prose-invert max-w-none">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PrivacyPolicyPage() {
  return (
    <PolicyPage
      title="Privacy Policy"
      content={
        <div className="space-y-6">
          <p className="text-lg">Effective Date: February 12, 2025</p>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">1. Introduction</h2>
            <p>Priority AI Services ("we," "us," or "our") is committed to protecting the privacy of our clients and website visitors. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with our automation solutions.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">2. Information We Collect</h2>
            <p>We may collect the following types of information:</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, company name, and any other information voluntarily provided.</li>
              <li><strong>Usage Data:</strong> Information automatically collected, including IP address, browser type, operating system, pages visited, and timestamps.</li>
              <li><strong>Chat Data:</strong> Conversations and interactions with our chat system, including lead capture information and customer inquiries.</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies to improve user experience and analyze website traffic.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">3. How We Use Your Information</h2>
            <p>We use the information collected to:</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Provide, operate, and improve our AI chatbot services.</li>
              <li>Respond to inquiries and support requests.</li>
              <li>Personalize user experience and improve website functionality.</li>
              <li>Conduct data analysis to enhance service performance.</li>
              <li>Comply with legal obligations and enforce our policies.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent personal information to third parties. However, we may share information with:</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li><strong>Service Providers:</strong> Third-party partners who assist in website operations, analytics, and AI technology enhancements.</li>
              <li><strong>Legal Compliance:</strong> If required by law, we may disclose information to regulatory authorities or law enforcement.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, sale, or business restructuring, client data may be transferred.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">5. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to safeguard personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">6. Data Retention</h2>
            <p>We retain personal data for as long as necessary to fulfill the purposes outlined in this policy unless a longer retention period is required by law.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">7. Your Rights and Choices</h2>
            <p>Depending on your location, you may have rights under data protection laws, including:</p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Accessing, updating, or deleting your personal information.</li>
              <li>Opting out of marketing communications.</li>
              <li>Restricting or objecting to data processing.</li>
              <li>Requesting data portability.</li>
            </ul>
            <p className="mt-4">To exercise your rights, contact us at priorityaiservices.info@gmail.com.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">8. Third-Party Links</h2>
            <p>Our website may contain links to third-party sites. We are not responsible for the privacy practices or content of these external websites.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">9. Updates to This Privacy Policy</h2>
            <p>We may update this Privacy Policy periodically. Any changes will be posted on this page with the revised effective date.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mt-8 mb-4">10. Contact Information</h2>
            <p>For questions about this Privacy Policy, contact us at:</p>
            <div className="mt-4">
              <p>Priority AI Services</p>
              <p>Email: priorityai.info@gmail.com</p>
              <p>Phone: (705) 307-6659</p>
            </div>
          </div>
        </div>
      }
    />
  );
}

export function TermsOfServicePage() {
  return (
    <PolicyPage
      title="Terms of Service"
      content={
        <div className="space-y-6">
          <p>Last updated: March 14, 2025</p>
          
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">1. Acceptance of Terms</h2>
          <p>By accessing and using our services, you agree to be bound by these Terms of Service and all applicable laws and regulations.</p>
          
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">2. Service Description</h2>
          <p>Priority AI Services provides automation solutions including lead rescue systems, chatbots, websites, and phone agents. We reserve the right to modify or discontinue any service at any time.</p>
          
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">3. User Responsibilities</h2>
          <p>You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.</p>
        </div>
      }
    />
  );
}

export function FAQPage() {
  return (
    <PolicyPage
      title="Frequently Asked Questions"
      content={
        <div className="space-y-8">
          <div>
            <h3 className="text-xl font-bold text-white mb-2">What does Priority AI Services actually do?</h3>
            <p>We help clinics and local service businesses get more clients with less busywork through:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Social Media Marketing (especially Meta/Facebook Ads) to generate new demand</li>
              <li>Lead Rescue & Automation to stop missed calls and slow follow-up from costing you bookings</li>
            </ul>
            <p className="mt-2">If you're not sure what you need, we'll diagnose it quickly and recommend the fastest path.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">What's the difference between "Social Media Marketing" and "Lead Rescue & Automation"?</h3>
            <p className="mb-2">They solve two different revenue problems:</p>
            <p className="font-semibold">Social Media Marketing (Growth):</p>
            <p className="mb-2">For businesses that need more new clients coming in.</p>
            <p className="font-semibold">Lead Rescue & Automation (Conversion):</p>
            <p>For businesses already getting inquiries but losing bookings due to missed calls, slow replies, weak follow-up, or no reminders.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">I'm at capacity. Do I still need any of this?</h3>
            <p className="mb-2">Maybe, but not always.</p>
            <p className="mb-2">If you truly don't want more clients, we can focus on:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Getting better-fit clients (higher value cases)</li>
              <li>Reducing no-shows and drop-offs</li>
              <li>Reactivating existing clients to fill gaps</li>
              <li>Saving time with lighter systems</li>
            </ul>
            <p className="mt-2">If you're already smooth and don't need growth, we'll tell you straight.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">Do I need to change my booking software or CRM?</h3>
            <p>Usually no. We build around what you already use (booking links, forms, existing phone number, etc.). The goal is simple improvement, not a full tech overhaul.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">How fast can this be set up?</h3>
            <p className="mb-2"><strong>Lead Rescue & Automation:</strong> typically quick once we have your details (often same day/next day depending on complexity).</p>
            <p><strong>Ads/Marketing:</strong> setup can be fast, but results improve over the first few weeks as we test and optimize.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">What do you need from me to get started?</h3>
            <p className="mb-2">Depends on the service, but typically:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your website + booking link</li>
              <li>Your preferred contact details</li>
              <li>Access to Meta Business tools (for ads), if applicable</li>
              <li>A few notes about your services and ideal client</li>
            </ul>
            <p className="mt-2">You'll get a simple setup form if we move forward.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">Do you work with personal phone numbers or do I need a separate business number?</h3>
            <p className="mb-2">Both are possible.</p>
            <p className="mb-2">If you're using your personal number for business, we can:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use a dedicated business number for automation so personal calls aren't mixed in</li>
              <li>Or set rules so the business system only triggers in specific situations</li>
            </ul>
            <p className="mt-2">We'll choose the cleanest option for your workflow.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">Do you guarantee results?</h3>
            <p className="mb-2">No one can guarantee results because it depends on your market, offer, and responsiveness. What we do guarantee is:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Clean setup</li>
              <li>Proper tracking</li>
              <li>Consistent optimization and reporting (where applicable)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">What is the "Business Audit" and what happens on the call?</h3>
            <p className="mb-2">It's a short call where we:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Identify where revenue is leaking (not enough demand vs poor follow-up)</li>
              <li>Recommend the quickest fix</li>
              <li>Give you next steps and pricing based on your situation</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">What should I select on the "Start Here" form?</h3>
            <p className="mb-2">Use this rule of thumb:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Need more new clients → Social Media Marketing</li>
              <li>Getting inquiries but not converting → Lead Rescue & Automation</li>
              <li>Not sure → General Audit / Not Sure Yet</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">How does pricing work?</h3>
            <p>Pricing depends on what you choose. Most services have either:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>A setup cost + monthly, or</li>
              <li>Monthly management (marketing)</li>
            </ul>
            <p className="mt-2">If you want, we can keep it simple and recommend the best starting point on the audit call.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">What if I need multiple services?</h3>
            <p>That's common. We usually start with the biggest revenue leak first, then add on once it's working. You won't be forced into a huge package upfront.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-white mb-2">How do I get started?</h3>
            <p>Use the Start Here form on the website, or book a quick audit call. After we review your info, we'll send clear next steps.</p>
          </div>
        </div>
      }
    />
  );
}

export function SupportMaintenancePage() {
  return (
    <PolicyPage
      title="Support & Maintenance Plan"
      content={
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">Standard Support Package</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>24/7 system monitoring</li>
            <li>Regular security updates</li>
            <li>Performance optimization</li>
            <li>Technical support during business hours</li>
            <li>Monthly system health reports</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">Premium Support Package</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Priority 24/7 technical support</li>
            <li>Custom feature development</li>
            <li>Quarterly strategy reviews</li>
            <li>Advanced analytics and reporting</li>
            <li>Dedicated support manager</li>
          </ul>
        </div>
      }
    />
  );
}

export function RefundPolicyPage() {
  return (
    <PolicyPage
      title="Refund & Cancellation Policy"
      content={
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">Service Cancellation</h2>
          <p>You may cancel your subscription service at any time. Cancellation will take effect at the end of the current billing period.</p>
          
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">Refund Policy</h2>
          <p>We offer a 30-day money-back guarantee for our Build & Bounce services. Subscription services are billed monthly and can be cancelled at any time.</p>
          
          <h2 className="text-2xl font-bold text-white mt-8 mb-4">Custom Projects</h2>
          <p>For custom development projects, refund policies are outlined in the project contract and vary based on project scope and completion status.</p>
        </div>
      }
    />
  );
}